import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";
import EditIcon from "@mui/icons-material/Edit";
import { getConfigs } from "../../apis/earnings";
import CountryRegionSelect from "../../components/CountryRegionSelect";
import { CountryType, RegionType } from "../../types/earnings";
import { getCountriesData } from "../../apis/master";
import "./style.css";
import { formatTextToCapitalise } from "../../utils/util";

const Earnings = () => {
  const { configs } = useSelector((state: any) => state.earningsReducer);
  const [cityConfigs, setCityConfigs] = useState(configs);
  const [filteredCityConfigs, setFilteredCityConfigs] = useState(configs);
  const [country, setCountry] = useState<CountryType | null>(null);
  const [city, setCity] = useState<RegionType | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  //function to handle navigation to edit page
  const navigateToEdit = (id: number) => {
    // Preserve country and city in URL when navigating to edit
    const countryParam = country?.country_code ? `?country_code=${encodeURIComponent(country.country_code)}` : "";
    const cityParam = city?.region_code ? `${countryParam ? "&" : "?"}region_code=${encodeURIComponent(city.region_code)}` : "";
    navigate(`/earnings-and-management/edit/${id}${countryParam}${cityParam}`);
  };

  // Function to filter the configs according to the selected country and region
  const filteredConfigs = () => {
    if (!country) {
      // If no country selected, show empty or all (we'll filter by default country)
      setFilteredCityConfigs([]);
      return;
    }

    setLoading(true);
    if (city) {
      // Filter by both country and city
      const filteredData = cityConfigs.filter(
        (item: any) =>
          item.country_code === country.country_code && item.region_code === city.region_code
      );
      setFilteredCityConfigs(filteredData.length > 0 ? filteredData : []);
    } else {
      // Filter by country only
      const filteredData = cityConfigs.filter(
        (item: any) => item.country_code === country.country_code
      );
      setFilteredCityConfigs(filteredData.length > 0 ? filteredData : []);
    }
    setLoading(false);
  };

  // Function to fetch and update configs data
  const getCityConfigs = async () => {
    const data = await getConfigs();
    setCityConfigs(data);
    // Don't set filteredCityConfigs here - let the filtering useEffect handle it
    // This ensures filtering happens after country is set by CountryRegionSelect
  };

  // CountryRegionSelect will handle restoration from URL params
  // We just need to sync URL params when country/city changes

  // Sync URL params when country or city changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    
    if (country?.country_code) {
      newSearchParams.set("country_code", country.country_code);
    } else {
      newSearchParams.delete("country_code");
    }
    
    if (city?.region_code) {
      newSearchParams.set("region_code", city.region_code);
    } else {
      newSearchParams.delete("region_code");
    }
    
    // Only update if params actually changed to avoid infinite loops
    const currentCountryCode = searchParams.get("country_code");
    const currentRegionCode = searchParams.get("region_code");
    
    if (
      (country?.country_code !== currentCountryCode) ||
      (city?.region_code !== currentRegionCode)
    ) {
      setSearchParams(newSearchParams);
    }
  }, [country, city]);

  // Filter data when country or city changes
  useEffect(() => {
    if (cityConfigs.length > 0 && country) {
      filteredConfigs();
    } else if (cityConfigs.length > 0 && !country) {
      // If no country selected yet, show empty (will be filtered when country is set by CountryRegionSelect)
      setFilteredCityConfigs([]);
    }
  }, [country, city, cityConfigs]);

  useEffect(() => {
    //API request
    getCityConfigs();
  }, []);

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box style={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <Typography style={{ fontWeight: 700, fontSize: 20 }}>
                  Earnings & Management
                </Typography>
              </div>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <CountryRegionSelect
                  country={country}
                  setCountry={setCountry}
                  city={city}
                  setCity={setCity}
                  urlCountryCode={searchParams.get("country_code")}
                  urlRegionCode={searchParams.get("region_code")}
                />
              </Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                    <TableRow>
                      <TableCell align="center" className="cellContainer">
                        Country
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Region
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Driver Ride Percentage
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Normal Driver Comission
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Premium Driver Comission
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Community Driver Comission Within 6 Months
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Community Driver Comission After 6 Months
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Cancelled Ride Comission
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Cancelled Shared Ride Comission
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Ride Cancellation Charges
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Shared Ride Cancellation Charges
                      </TableCell>
                      <TableCell align="center" className="cellContainer">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginTop: "20px",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      filteredCityConfigs &&
                      filteredCityConfigs.map((row: any) => {
                        return (
                          <TableRow key={row._id}>
                            <TableCell align="center">{formatTextToCapitalise(row.country_code)}</TableCell>
                            <TableCell align="center">{formatTextToCapitalise(row.region_code)}</TableCell>
                            <TableCell align="center">{row.driver_ride_percentage}%</TableCell>
                            <TableCell align="center">{row.normal_driver_comission}%</TableCell>
                            <TableCell align="center">{row.premium_driver_comission}%</TableCell>
                            <TableCell align="center">
                              {row.community_driver_comission_within_six_months}%
                            </TableCell>
                            <TableCell align="center">
                              {row.community_driver_comission_after_six_months}%
                            </TableCell>
                            <TableCell align="center">{row.cancelled_ride_comission}%</TableCell>
                            <TableCell align="center">
                              {row.cancelled_shared_ride_comission}%
                            </TableCell>
                            <TableCell align="center">{row.ride_cancellation_charges}</TableCell>
                            <TableCell align="center">
                              {row.shared_ride_cancellation_charges}
                            </TableCell>
                            <TableCell align="center" style={{ cursor: "pointer" }}>
                              <EditIcon onClick={() => navigateToEdit(row._id)} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Earnings;
